import { createEffect, createEvent, createStore, sample } from 'effector'
import { $goods, IInitGroupGoods } from '@entities/goods/model'
import { IShipment } from 'src/shared/contract/shipment'
import { IBaseCatalogQuery, NextContext } from 'src/shared/contract/type'
import { ICatalogGoodBase, IFullCatalog, IGroupItem } from '@entities/goods/contract'
import { getGoodGroupById, IRequestGoodSearch, searchGoods } from '@entities/goods/api'
import { $shipment } from '@entities/shipment/model'
import { $currentGroupId } from '@entities/groups/model'
import { $currentPath, $isCatalogPage, onRouteChangeComplete, onRouteChangeStart } from '@entities/route/model'
import { IRequestGroupSearch, searchGroups } from '@entities/groups/api'
import { IGroup } from '@entities/groups/contract'
import { subscribeLoggerEffect } from 'src/infastruct/effector-utils/subscribe-sentry'
import { $router } from 'src/infastruct/effector-utils/attach-router'

interface IGroupQuery extends IBaseCatalogQuery {
    groupId: number
}

export const initGroupGoods = createEvent<IInitGroupGoods>()
export const initSearchGroupGoods = createEvent<IRequestGoodSearch>()
export const updateGroupGoods = createEvent()
export const resetGroup = createEvent()
export const onEmptySearchGoods = createEvent()
export const $groups = createStore<IGroupItem[]>([] as IGroupItem[]).reset(resetGroup)

export const $foundGroups = createStore<IGroup[]>([])

export const $searchTerm = createStore<string>('').reset(onRouteChangeStart)

export const setSearchTerm = createEvent<string>()

sample({
    clock: setSearchTerm,
    target: $searchTerm
})

const goodsGroupInitFx = createEffect<IGroupQuery, IFullCatalog, Error>(async ({ groupId, shipment, ctx }) => {
    const fullCatalog = await getGoodGroupById({ groupId, shipment, ctx })
    return fullCatalog as IFullCatalog
})
subscribeLoggerEffect(goodsGroupInitFx)
export const $isPendingGroup = goodsGroupInitFx.pending

const goodsSearchFx = createEffect<IRequestGoodSearch, ICatalogGoodBase[], Error>(async (args) => {
    const goods = await searchGoods(args)
    return goods as ICatalogGoodBase[]
})
subscribeLoggerEffect(goodsSearchFx)

const groupsSearchFx = createEffect<IRequestGroupSearch, IGroup[], Error>(async (args) => {
    return await searchGroups(args)
})
export const $isPendingSearch = groupsSearchFx.pending
subscribeLoggerEffect(groupsSearchFx)

sample({
    clock: initGroupGoods,
    fn: ({ groupId }) => groupId,
    target: $currentGroupId
})

sample({
    clock: initGroupGoods,
    source: $shipment,
    fn: (shipment: IShipment, { groupId, ctx }: IInitGroupGoods) => ({ groupId, shipment, ctx }),
    target: goodsGroupInitFx
})

sample({
    clock: goodsGroupInitFx.doneData,
    fn: ({ goods }: IFullCatalog) => goods,
    target: $goods
})
sample({
    clock: goodsSearchFx.doneData,
    target: $goods
})

sample({
    clock: goodsSearchFx.doneData,
    filter: (goods) => goods.length === 0,
    target: onEmptySearchGoods
})

sample({
    clock: goodsGroupInitFx.doneData,
    fn: ({ groups }: IFullCatalog) => groups,
    target: $groups
})

sample({
    clock: groupsSearchFx.doneData,
    target: $foundGroups
})

sample({
    clock: initSearchGroupGoods,
    target: goodsSearchFx
})

sample({
    clock: $shipment,
    source: { router: $router, path: $currentPath },
    filter: ({ path }) => path.includes('/catalog/search'),
    fn: ({ router }, shipment): IRequestGoodSearch => ({ term: router!.query?.q as string, shipment }),
    target: initSearchGroupGoods
})

sample({
    clock: $currentPath,
    filter: (_, path) => path.includes('/catalog/search'),
    source: { shipment: $shipment, searchTerm: $searchTerm },
    fn: ({ shipment, searchTerm }) => ({ shipment, term: searchTerm } as IRequestGroupSearch),
    target: groupsSearchFx
})

sample({
    clock: updateGroupGoods,
    source: [$currentGroupId, $shipment],
    filter: $isCatalogPage,
    fn: ([groupId, shipment]) => ({ groupId, shipment } as { groupId: number; shipment: IShipment; ctx?: NextContext }),
    target: goodsGroupInitFx
})

sample({
    clock: $shipment,
    source: $currentPath,
    // filter: (dn) => dn.operation === 'set',
    filter: (path) =>
        !path.includes('/catalog/suggestions') &&
        !path.includes('/catalog/promotion') &&
        !path.includes('/catalog/liked') &&
        !path.includes('/catalog/search'),
    target: updateGroupGoods
})
